import { Button } from '@/components/ui/button'
import { ApiError } from '@/lib/api'
import { CobaltLaunchButton } from './CobaltLaunchButton'
import type { CobaltConnectionStatus } from './types'

interface CobaltStatusPanelProps {
  status?: CobaltConnectionStatus
  error?: Error | null
  canManage: boolean
  isStarting: boolean
  isRetrying: boolean
  onRetry: () => void
  onStartOnboarding: () => void
}

export function CobaltStatusPanel({
  status,
  error,
  canManage,
  isStarting,
  isRetrying,
  onRetry,
  onStartOnboarding,
}: CobaltStatusPanelProps) {
  const needsReauthentication =
    status === 'needs_reauthentication' || status === 'invalid_token'
  const unavailable =
    !!error || status === 'unavailable' || status === undefined
  const errorDetail =
    error instanceof ApiError
      ? `${error.message} (HTTP ${error.status})`
      : error?.message

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <h3 className="font-medium text-primary-high-contrast">
        Data synchronization
      </h3>
      {unavailable ? (
        <div className="mt-2 space-y-3">
          <p className="text-sm text-muted-foreground">
            The Cobalt connection could not be confirmed. Retry the check before
            starting onboarding.
          </p>
          {errorDetail && (
            <p className="text-xs text-muted-foreground">{errorDetail}</p>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isRetrying}
            onClick={onRetry}>
            {isRetrying ? 'Checking…' : 'Retry'}
          </Button>
        </div>
      ) : status === 'not_connected' || needsReauthentication ? (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            {needsReauthentication
              ? 'The Cobalt connection needs a new sign-in to synchronize data.'
              : 'Connect eClinicalWorks through Cobalt to synchronize data for Careforce workflows.'}
          </p>
          {canManage ? (
            <CobaltLaunchButton
              size="sm"
              className="mt-3"
              isStarting={isStarting}
              onClick={onStartOnboarding}>
              {needsReauthentication
                ? 'Reconnect through Cobalt'
                : 'Connect through Cobalt'}
            </CobaltLaunchButton>
          ) : (
            <p className="mt-3 text-sm text-muted-foreground">
              Ask an organization admin to connect Cobalt.
            </p>
          )}
        </>
      ) : null}
    </div>
  )
}
